import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { EmptyState } from '@ets/design-system';

import { InstanceService } from '_common/services';
import { useDispatch, useSelector } from '_common/hooks';
import { listObjects } from '_common/components/Table/TableSlice';

import {
  ThunksTable,
  Cell,
  SelectionCounter,
  Footer,
  type TableColumn,
  type TableRow,
} from '_common/components/Table';
import SpaceCell from './SpaceCell';
import OriginalLocationCell from './OriginalLocationCell';
import UserNameCell from './UserNameCell';

const PAGE_IDENTITY = 'recycle';

const Table = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const list = useSelector((state) => state.table.identity.recycle.list);
  const appData = useSelector((state) => state.app.data);

  //#region Table object listing handlers
  const fetchObjects = useCallback((parameters: any) => {
    return new InstanceService().listRecycle(parameters);
  }, []);

  useEffect(() => {
    dispatch(
      listObjects({
        identity: PAGE_IDENTITY,
        fetch: fetchObjects,
        cause: 'INITIAL',
        request: { offset: 0 },
      }),
    );
  }, []);

  // No useEffectOnUpdate since fetchObjects doesn't have any dependencies
  // Add if dependencies are added to fetchObjects

  //#endregion

  const columns = useMemo<TableColumn<Objekt>[]>(() => {
    return [
      {
        id: 'name',
        accessorFn: (object) => object.object?.name,
        header: intl.formatMessage({ id: 'storage.browserHeader.name' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-name-column`}>
              <Cell.ObjectName object={object} testId={`${object.id}-object-name`} />
            </Cell>
          );
        },
      },
      {
        id: 'space',
        accessorFn: (object) => object.object?.space?.personal || object.object?.space?.name,
        enableSorting: false,
        size: 144,
        header: intl.formatMessage({ id: 'storage.browserHeader.space' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-space-column`}>
              <SpaceCell object={object.object} />
            </Cell>
          );
        },
      },
      {
        id: 'originalLocation',
        accessorFn: (object) => object.object?.parent?.name,
        size: 160,
        enableSorting: false,
        header: intl.formatMessage({ id: 'storage.browserHeader.originalLocation' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-originalLocation-column`}>
              <OriginalLocationCell object={object.object} />
            </Cell>
          );
        },
      },
      {
        id: 'date',
        accessorFn: (object) => object.date,
        size: 160,
        header: intl.formatMessage({ id: 'DATE_DELETED' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-date-deleted-column`}>
              <Cell.DateTime date={{ date: object.date }} time={{ time: object.date }} />
            </Cell>
          );
        },
      },
      {
        id: 'deletedBy',
        accessorKey: 'creator',
        enableSorting: false,
        size: 144,
        header: intl.formatMessage({ id: 'storage.browserHeader.deletedBy' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-deletedBy-column`}>
              <UserNameCell userId={object.creator} />
            </Cell>
          );
        },
      },
    ];
  }, [appData]);

  const renderEmptyState = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', flex: 1, alignItems: 'center' }}>
        <EmptyState
          size="l"
          icon="trash"
          title={intl.formatMessage({ id: 'storage.browserHeader.recycleBinEmpty' })}
          description={intl.formatMessage({ id: 'storage.browserHeader.recycleBinEmptySub' })}
          testId="recycle-page-empty-list"
        />
      </div>
    );
  };

  const rowsData = useMemo(() => {
    return list.map<TableRow<Objekt>>((objectId) => {
      return appData[objectId];
    });
  }, [list, appData]);

  return (
    <ThunksTable
      identity={PAGE_IDENTITY}
      fetchObjects={fetchObjects}
      options={{ columns, data: rowsData }}
      lazyLoading
      renderFooter={() => (
        <Footer>
          <SelectionCounter identity={PAGE_IDENTITY} />
        </Footer>
      )}
      renderEmptyState={renderEmptyState}
      loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
      testId="recycle"
    />
  );
};

export default Table;
